import React, { useMemo, useState } from "react";
import { ChevronRightIcon } from "lucide-react";
import { useSquareCatalog } from "../hooks/useSquareCatalog";
import { SquareCatalogProduct } from "../types/SquareCatalog";
import { ProductGroup, formatPrice, formatPriceRange, getSizeLabel, groupProducts } from "../utils/productGroups";
import { GlitterSparkles } from "./GlitterSparkles";
import { ProductGroupDetail } from "./ProductGroupDetail";
import { ProductImage } from "./ProductImage";
import { QuantityControl } from "./QuantityControl";
import { SearchForm } from "./SearchForm";
type CatalogListingPageProps = {
  title: string;
  description: string;
  keywords?: string[];
  /** Regex patterns tested against name + variation + description; a product must match at least one (when provided). */
  include?: string[];
  /** Regex patterns tested against name + variation + description; products matching any are hidden. */
  exclude?: string[];
  /** Regex patterns tested against the product name only; a product must match at least one (when provided). */
  nameInclude?: string[];
  /** Regex patterns tested against the product name only; products matching any are hidden. */
  nameExclude?: string[];
  /** Products that belong to any of these categories are hidden (used by the All That Sparkles catch-all). */
  excludeCategories?: ProductFilters[];
  /** Free-text search; every word must appear in the product's name, variation, or description. */
  query?: string;
  /** Shows a search box under the page header. */
  searchBox?: {
    initialQuery: string;
    onSearch: (query: string) => void;
  };
  /** When false, every product shows as its own listing instead of being grouped by size. */
  groupVariations?: boolean;
  backLabel?: string;
  onBack: () => void;
};
const noCategories: ProductFilters[] = [];
export function CatalogListingPage({
  title,
  description,
  keywords = [],
  include = [],
  exclude = [],
  nameInclude = [],
  nameExclude = [],
  excludeCategories = noCategories,
  query,
  searchBox,
  groupVariations = true,
  backLabel = "Back to home",
  onBack
}: CatalogListingPageProps) {
  const {
    products,
    isLoading,
    error
  } = useSquareCatalog();
  const [openGroupKey, setOpenGroupKey] = useState<string | null>(null);
  const isSearch = query !== undefined;
  const searchText = (query ?? "").trim();
  const listings = useMemo(() => {
    if (isSearch && !searchText) {
      return [];
    }
    const filtered = filterProducts(products, {
      keywords,
      include,
      exclude,
      nameInclude,
      nameExclude
    }, excludeCategories);
    return isSearch ? searchProducts(filtered, searchText) : filtered;
  }, [keywords, include, exclude, nameInclude, nameExclude, excludeCategories, products, isSearch, searchText]);
  const groups = useMemo<ProductGroup[]>(() => groupVariations ? groupProducts(listings) : listings.map(product => ({
    key: product.variationId,
    name: product.name,
    products: [product],
    labels: {
      [product.variationId]: getSizeLabel(product)
    }
  })), [listings, groupVariations]);
  const openGroup = groups.find(group => group.key === openGroupKey) ?? null;
  const emptyMessage = isSearch ? searchText ? `No products match “${searchText}”. Try a different word, like “tumbler” or “pen”.` : "Search for a product by name, style, or color." : "No products are available in this category right now.";
  const openGroupDetail = (key: string) => {
    setOpenGroupKey(key);
    window.scrollTo({
      top: 0
    });
  };
  const closeGroupDetail = () => {
    setOpenGroupKey(null);
    window.scrollTo({
      top: 0
    });
  };
  return <main className="relative min-h-[100dvh] w-full bg-[#10070f] px-4 py-10 text-white sm:px-8 sm:py-14" data-id="element-136" data-mp="38p7ob4vzdll8">
      <div className="pointer-events-none fixed inset-0" aria-hidden="true" data-id="element-137" data-mp="3dniyxhu3o04i">
        <GlitterSparkles count={450} data-id="element-138" data-mp="16xl3xm6lgoxq" />
      </div>
      <div className="relative mx-auto max-w-7xl" data-id="element-139" data-mp="16yw6xjs7tsns">
        {openGroup ? <ProductGroupDetail group={openGroup} backLabel={`Back to ${title}`} onBack={closeGroupDetail} data-id="element-140" data-mp="1bwxqs6xgtw0b" /> : <>
            <button type="button" onClick={onBack} className="mb-8 text-sm font-medium text-[#35dceb] underline decoration-transparent underline-offset-4 transition-colors duration-150 ease-out hover:decoration-current focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#35dceb]" data-id="element-141" data-mp="37z15ldnwvhqi">
              {backLabel}
            </button>
            <header className="border-b border-[#ff3aa2]/60 pb-6" data-id="element-142" data-mp="2ka9j92x05tln">
              <h1 className="text-3xl font-semibold tracking-tight text-[#ff5bad] sm:text-4xl" data-id="element-143" data-mp="2k8yfh33k4luj">{title}</h1>
              <p className="mt-2 text-sm text-white/70" data-id="element-144" data-mp="2g6jxel7ppq4b">{description}</p>
            </header>

            {searchBox && <div className="mt-6 max-w-xl" data-id="element-145" data-mp="26aguve8y7pgu">
                <SearchForm initialQuery={searchBox.initialQuery} onSearch={searchBox.onSearch} data-id="element-146" data-mp="2695i4oxiz7dl" />
              </div>}

            {isLoading && <CatalogMessage message="Loading live catalog…" data-id="element-147" data-mp="21dq8682n1vmg" />}
            {error && <CatalogMessage message={error} tone="error" data-id="element-148" data-mp="1r773gxzqsrfi" />}
            {!isLoading && !error && groups.length === 0 && <CatalogMessage message={emptyMessage} data-id="element-149" data-mp="1w8mpotdnp3zh" />}
            {!isLoading && !error && isSearch && groups.length > 0 && <p className="mt-6 text-sm text-white/60" data-id="element-150" data-mp="264fyc1o98dbz">
                {groups.length} {groups.length === 1 ? "result" : "results"}
              </p>}

            {!isLoading && !error && groups.length > 0 && <section className="mt-8" aria-label={`${title} listings`} data-id="element-151" data-mp="1dh3n2nnjnrxz">
                <div className="grid grid-cols-1 gap-x-5 gap-y-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-id="element-152" data-mp="1ifew4w60lyf5">
                  {groups.map(group => group.products.length > 1 ? <GroupCard key={group.key} group={group} onOpen={() => openGroupDetail(group.key)} data-id="element-153" data-mp="pqzlsmxtw4a5" /> : <SingleProductCard key={group.key} product={group.products[0]} data-id="element-154" data-mp="uo9f4doc9039" />)}
                </div>
              </section>}
          </>}
      </div>
    </main>;
}
function GroupCard({
  group,
  onOpen
}: {
  group: ProductGroup;
  onOpen: () => void;
}) {
  const lead = group.products.find(product => product.imageUrl) ?? group.products[0];
  const sizes = group.products.map(product => group.labels[product.variationId] ?? getSizeLabel(product));
  const isSoldOut = group.products.every(product => product.inventory <= 0);
  return <button type="button" onClick={onOpen} className="group flex min-w-0 flex-col border border-[#ff3aa2]/35 bg-[#1b0b1a] p-3 text-left transition-colors duration-150 ease-out hover:border-[#35dceb] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#35dceb]" aria-label={`${group.name}, ${sizes.length} sizes. Choose a size`} data-id="element-155" data-mp="3uxuzqd90rj9b">
      <ProductImage product={lead} alt={group.name} data-id="element-156" data-mp="1k3z7kcfr17c4" />
      <div className="flex flex-1 flex-col pt-4" data-id="element-157" data-mp="1rni7pvh4jh7x">
        <h3 className="text-base font-medium leading-5 text-white" data-id="element-158" data-mp="1rpum5ynob9g8">{group.name}</h3>
        <p className="mt-1 text-sm text-[#35dceb]" data-id="element-159" data-mp="1wofn60ar1x5q">
          {sizes.length} sizes · {sizes.join(", ")}
        </p>
        <p className="mt-4 text-lg font-semibold text-[#ff5bad]" data-id="element-160" data-mp="1ip69p8dxhh5g">{formatPriceRange(group.products)}</p>
        <div className="mt-auto pt-4" data-id="element-161" data-mp="1mqrjgkv61fbf">
          <div className="flex items-center justify-between border-t border-white/15 pt-3 text-sm" data-id="element-162" data-mp="2adrb38ondf17">
            {isSoldOut ? <span className="font-semibold text-[#ff5bad]" data-id="element-163" data-mp="2fcawn7zbwot1">Sold Out</span> : <span className="font-semibold text-white" data-id="element-164" data-mp="21lxr1c9wtm6v">Choose size</span>}
            <ChevronRightIcon size={18} className="text-[#35dceb] transition-transform duration-150 ease-out group-hover:translate-x-0.5" aria-hidden="true" data-id="element-165" data-mp="21juyakhdheyg" />
          </div>
        </div>
      </div>
    </button>;
}
function SingleProductCard({
  product
}: {
  product: SquareCatalogProduct;
}) {
  return <article data-square-variation-id={product.variationId} className="flex min-w-0 flex-col border border-[#ff3aa2]/35 bg-[#1b0b1a] p-3" data-id="element-166" data-mp="2y5ojsqwsvqy3">
      <ProductImage product={product} data-id="element-167" data-mp="24m7kajkb7rvz" />
      <div className="flex flex-1 flex-col pt-4" data-id="element-168" data-mp="24l682brk4zco">
        <h3 className="text-base font-medium leading-5 text-white" data-id="element-169" data-mp="1zmv0k7oqj0xm">{product.name}</h3>
        <p className="mt-1 text-sm text-[#35dceb]" data-id="element-170" data-mp="1uojorrevqgcr">{product.variationName}</p>
        {product.description && <p className="mt-3 text-sm leading-5 text-white/70" data-id="element-171" data-mp="2sbs414oaet6b">{product.description}</p>}
        <p className="mt-4 text-lg font-semibold text-[#ff5bad]" data-id="element-172" data-mp="2ncz1u8egaolg">{formatPrice(product.priceCents, product.currency)}</p>
        <div className="mt-auto pt-4" data-id="element-173" data-mp="2idco7xupn6c4">
          <div className="border-t border-white/15 pt-3" data-id="element-174" data-mp="2icb2mzg2j1g5">
            <QuantityControl product={product} label={product.name} data-id="element-175" data-mp="2dfaw6nfb3woi" />
          </div>
        </div>
      </div>
    </article>;
}
function searchProducts(products: SquareCatalogProduct[], query: string): SquareCatalogProduct[] {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  return products.filter(product => {
    const text = `${product.name} ${product.variationName} ${product.description}`.toLowerCase();
    return terms.every(term => text.includes(term));
  });
}

/** The rules a category page uses to decide which Square products belong to it. */
export type ProductFilters = {
  keywords?: string[];
  include?: string[];
  exclude?: string[];
  nameInclude?: string[];
  nameExclude?: string[];
};

/** Builds a test that says whether a product belongs to a category with these rules. */
function createMatcher(filters: ProductFilters) {
  const normalizedKeywords = (filters.keywords ?? []).map(keyword => keyword.toLowerCase());
  const toRegex = (patterns: string[] = []) => patterns.map(pattern => new RegExp(pattern, "i"));
  const include = toRegex(filters.include);
  const exclude = toRegex(filters.exclude);
  const nameInclude = toRegex(filters.nameInclude);
  const nameExclude = toRegex(filters.nameExclude);
  return (product: SquareCatalogProduct) => {
    const fullText = `${product.name} ${product.variationName} ${product.description}`;
    const lower = fullText.toLowerCase();
    if (normalizedKeywords.length > 0 && !normalizedKeywords.some(keyword => lower.includes(keyword))) {
      return false;
    }
    if (include.length > 0 && !include.some(pattern => pattern.test(fullText))) {
      return false;
    }
    if (exclude.some(pattern => pattern.test(fullText))) {
      return false;
    }
    if (nameInclude.length > 0 && !nameInclude.some(pattern => pattern.test(product.name))) {
      return false;
    }
    return !nameExclude.some(pattern => pattern.test(product.name));
  };
}
function filterProducts(products: SquareCatalogProduct[], filters: ProductFilters, otherCategories: ProductFilters[]): SquareCatalogProduct[] {
  const matches = createMatcher(filters);
  const otherMatchers = otherCategories.map(createMatcher);
  return products.filter(product => matches(product) && !otherMatchers.some(inOtherCategory => inOtherCategory(product)));
}
function CatalogMessage({
  message,
  tone = "default"
}: {
  message: string;
  tone?: "default" | "error";
}) {
  return <p className={`mt-8 border p-4 text-sm ${tone === "error" ? "border-[#ff5bad]/60 bg-[#3a1232] text-white" : "border-white/15 bg-[#1b0b1a] text-white/75"}`} data-id="element-176" data-mp="fp7l4t6bah6p">{message}</p>;
}